import { useRef, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Upload, X, Send, Loader2, Calendar } from "lucide-react";
import { CONTACT, SERVICE_OPTIONS } from "@/data/site";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    service: "",
    preferredDate: "",
    description: "",
  });
  const [photos, setPhotos] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef(null);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onPickFiles = (e) => {
    const list = Array.from(e.target.files || []);
    setPhotos((prev) => [...prev, ...list].slice(0, 6));
  };

  const removePhoto = (idx) => {
    setPhotos((prev) => prev.filter((_, i) => i !== idx));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.description) {
      toast.error("Please fill out all required fields.");
      return;
    }

    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("name", form.name);
      fd.append("phone", form.phone);
      fd.append("email", form.email);
      fd.append("service", form.service);
      fd.append("preferred_date", form.preferredDate);
      fd.append("description", form.description);
      photos.forEach((file) => fd.append("photos", file));

      const res = await axios.post(`${API}/contact`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (res.status === 200 || res.status === 201) {
        toast.success("Request received! We'll get back to you shortly.");
        setForm({
          name: "",
          phone: "",
          email: "",
          service: "",
          preferredDate: "",
          description: "",
        });
        setPhotos([]);
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Something went wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="rs-section bg-white">
      <div className="rs-container">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14">
          {/* Left — info */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-5"
          >
            <p className="rs-overline mb-4">Get In Touch</p>
            <h2 className="font-display font-semibold text-3xl sm:text-4xl lg:text-5xl text-[#0A192F] leading-tight">
              Request your{" "}
              <span className="text-[#F97316]">free estimate.</span>
            </h2>
            <p className="mt-5 text-slate-600 text-base md:text-lg leading-relaxed">
              Tell us about your project and add a few photos if you have them. We&apos;ll get
              back to you fast with an honest estimate.
            </p>

            <div
              data-testid="first-time-discount-banner"
              className="mt-6 flex items-start gap-3 rounded-2xl border border-[#F97316]/30 bg-[#F97316]/[0.06] p-4"
            >
              <div className="h-9 w-9 rounded-lg bg-[#F97316] text-white grid place-items-center flex-shrink-0">
                <span className="font-display font-bold text-sm">5%</span>
              </div>
              <div>
                <div className="font-display font-semibold text-[#0A192F]">
                  First-time customer? Save 5% off your first project.
                </div>
                <p className="text-sm text-slate-600 mt-0.5">
                  Mention this offer when you request your estimate.
                </p>
              </div>
            </div>

            <div className="mt-10 space-y-5">
              <ContactRow
                icon={Phone}
                label="Call or Text"
                value={CONTACT.phone}
                href={`tel:${CONTACT.phoneRaw}`}
                testId="contact-phone"
              />
              <ContactRow
                icon={Phone}
                label="Alt Phone"
                value={CONTACT.altPhone}
                href={`tel:${CONTACT.altPhoneRaw}`}
                testId="contact-phone-alt"
              />
              <ContactRow
                icon={Mail}
                label="Email"
                value={CONTACT.emailPrimary}
                href={`mailto:${CONTACT.emailPrimary}`}
                testId="contact-email-primary"
              />
              <ContactRow
                icon={Mail}
                label="Alt Email"
                value={CONTACT.emailSecondary}
                href={`mailto:${CONTACT.emailSecondary}`}
                testId="contact-email-secondary"
              />
              <ContactRow
                icon={MapPin}
                label="Service Area"
                value={CONTACT.serviceArea}
                testId="contact-area"
              />
            </div>
          </motion.div>

          {/* Right — form */}
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-7"
          >
            <form
              onSubmit={onSubmit}
              data-testid="contact-form"
              className="bg-[#F8FAFC] rounded-3xl p-7 md:p-10 border border-slate-200"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <Field
                  label="Name *"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Your full name"
                  testId="input-name"
                />
                <Field
                  label="Phone *"
                  type="tel"
                  value={form.phone}
                  onChange={update("phone")}
                  placeholder="(555) 555-5555"
                  testId="input-phone"
                />
              </div>
              <div className="mt-5">
                <Field
                  label="Email *"
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@example.com"
                  testId="input-email"
                />
              </div>
              <div className="mt-5 grid sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Service
                  </label>
                  <select
                    value={form.service}
                    onChange={update("service")}
                    data-testid="input-service"
                    className="w-full rounded-xl bg-white border border-slate-200 px-4 py-3.5 text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#F97316]/40 focus:border-[#F97316] transition-all"
                  >
                    <option value="">Select a service…</option>
                    {SERVICE_OPTIONS.map((opt) => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Preferred date
                  </label>
                  <div className="relative">
                    <input
                      type="date"
                      value={form.preferredDate}
                      onChange={update("preferredDate")}
                      data-testid="input-preferred-date"
                      className="w-full rounded-xl bg-white border border-slate-200 px-4 py-3.5 pr-11 text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#F97316]/40 focus:border-[#F97316] transition-all"
                    />
                    <Calendar className="absolute right-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-[#F97316] pointer-events-none" />
                  </div>
                </div>
              </div>
              <div className="mt-5">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Project Description *
                </label>
                <textarea
                  value={form.description}
                  onChange={update("description")}
                  data-testid="input-description"
                  rows={5}
                  placeholder="Tell us about the project — location, timeline, and any details that help."
                  className="w-full rounded-xl bg-white border border-slate-200 px-4 py-3.5 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#F97316]/40 focus:border-[#F97316] transition-all"
                />
              </div>

              {/* Upload */}
              <div className="mt-5">
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Upload Photos (optional, up to 6)
                </label>
                <div className="flex items-center gap-3 flex-wrap">
                  <button
                    type="button"
                    data-testid="upload-photos-btn"
                    onClick={() => fileInputRef.current?.click()}
                    className="inline-flex items-center gap-2 rounded-xl border border-dashed border-slate-300 bg-white hover:border-[#F97316] hover:bg-[#F97316]/5 text-slate-700 px-4 py-3 text-sm font-medium transition-all"
                  >
                    <Upload className="h-4 w-4 text-[#F97316]" />
                    Add photos
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    multiple
                    className="hidden"
                    onChange={onPickFiles}
                    data-testid="file-input"
                  />
                  <span className="text-xs text-slate-500">JPG, PNG, WEBP</span>
                </div>

                {photos.length > 0 && (
                  <div className="mt-4 flex flex-wrap gap-3" data-testid="photo-preview-list">
                    {photos.map((f, idx) => (
                      <div
                        key={idx}
                        className="relative h-20 w-20 rounded-lg overflow-hidden border border-slate-200 bg-white"
                        data-testid={`photo-preview-${idx}`}
                      >
                        <img
                          src={URL.createObjectURL(f)}
                          alt={`upload ${idx + 1}`}
                          className="h-full w-full object-cover"
                        />
                        <button
                          type="button"
                          onClick={() => removePhoto(idx)}
                          data-testid={`remove-photo-${idx}`}
                          className="absolute -top-1.5 -right-1.5 h-6 w-6 rounded-full bg-[#0A192F] text-white grid place-items-center shadow-md hover:bg-[#F97316] transition-colors"
                          aria-label="Remove photo"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="mt-8 rs-btn-primary w-full sm:w-auto justify-center disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending…
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4" />
                    Request a Free Estimate
                  </>
                )}
              </button>

              <p className="mt-4 text-xs text-slate-500">
                By submitting, you agree to be contacted about your project. We never share your info.
              </p>
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function Field({ label, testId, ...props }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-2">{label}</label>
      <input
        {...props}
        data-testid={testId}
        className="w-full rounded-xl bg-white border border-slate-200 px-4 py-3.5 text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#F97316]/40 focus:border-[#F97316] transition-all"
      />
    </div>
  );
}

function ContactRow({ icon: Icon, label, value, href, testId }) {
  const Wrapper = href ? "a" : "div";
  return (
    <Wrapper
      href={href}
      data-testid={testId}
      className="flex items-center gap-4 group"
    >
      <div className="h-11 w-11 rounded-xl bg-[#F97316]/10 grid place-items-center group-hover:bg-[#F97316] transition-colors flex-shrink-0">
        <Icon className="h-5 w-5 text-[#F97316] group-hover:text-white transition-colors" />
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest font-semibold text-slate-500">
          {label}
        </div>
        <div className="text-base font-medium text-[#0A192F] group-hover:text-[#F97316] transition-colors">
          {value}
        </div>
      </div>
    </Wrapper>
  );
}
